import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Calendar, DollarSign, Package, Plus, CheckCircle2, AlertCircle, CheckSquare, FileText, HandHeart, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SubmitExpenseClaimDialog } from "@/components/SubmitExpenseClaimDialog";
import { format } from "date-fns";
import { useDocumentsAccess } from "@/hooks/useDocumentsAccess";
import { useGoogleIntegration } from "@/hooks/useGoogleIntegration";
import { DocumentsTabContent } from "@/components/DocumentsTabContent";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Helping Button Component
const HelpingButton = ({ eventId }: { eventId: string }) => {
  const [isHelping, setIsHelping] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkIfHelping();
  }, [eventId]);

  const checkIfHelping = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: memberData } = await supabase
      .from('members')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData) {
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('event_helpers')
      .select('id')
      .eq('event_id', eventId)
      .eq('member_id', memberData.id)
      .maybeSingle();

    setIsHelping(!!data);
    setLoading(false);
  };

  const toggleHelping = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: memberData } = await supabase
      .from('members')
      .select('id, pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData) return;

    if (isHelping) {
      // Remove as helper
      await supabase
        .from('event_helpers')
        .delete()
        .eq('event_id', eventId)
        .eq('member_id', memberData.id);
      setIsHelping(false);
      toast.success("You're no longer signed up to help");
    } else {
      // Add as helper
      await supabase
        .from('event_helpers')
        .insert({
          event_id: eventId,
          pta_id: memberData.pta_id,
          member_id: memberData.id
        });
      setIsHelping(true);
      toast.success("Thanks for volunteering!");
    }
  };

  if (loading) return null;

  return (
    <Button
      variant={isHelping ? "default" : "outline"}
      size="sm"
      onClick={toggleHelping}
      className={`flex items-center gap-1 ${isHelping ? "bg-green-500 hover:bg-green-600" : ""}`}
    >
      <HandHeart className="h-4 w-4" />
      {isHelping ? "Helping!" : "Helping?"}
    </Button>
  );
};
export default function Dashboard() {
  const { hasPlan } = useAuth();
  const [sponsorshipData, setSponsorshipData] = useState<any>(null);
  const [subscriptionSponsoredBy, setSubscriptionSponsoredBy] = useState<string | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [stats, setStats] = useState({
    latestMemberName: "",
    latestMemberRole: "",
    upcomingEvents: 0,
    fundsRaised: "£0",
    totalMembers: 0
  });
  const [activeGoals, setActiveGoals] = useState<any[]>([]);
  const [goalsByStatus, setGoalsByStatus] = useState({
    active: [] as any[],
    up_next: [] as any[],
    planned: [] as any[],
    submitted: [] as any[]
  });
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [myExpenseClaims, setMyExpenseClaims] = useState<any[]>([]);
  const [hasExpenseAccess, setHasExpenseAccess] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [selectedGoalStatus, setSelectedGoalStatus] = useState<'active' | 'up_next' | 'planned' | 'submitted'>('active');
  const [isSyncingCalendar, setIsSyncingCalendar] = useState(false);
  
  // Documents access
  const { hasAccess: hasDocumentsAccess, isReadOnly: isDocumentsReadOnly, ptaId: documentsPtaId } = useDocumentsAccess();
  
  // Google integration - uses ptaId from documents access
  const { isDriveEnabled, isConnected: isGoogleConnected, isCalendarEnabled, connectGoogle } = useGoogleIntegration(documentsPtaId);
  
  // Show documents tab when user has documents access
  const showDocumentsTab = hasDocumentsAccess;
  
  // Check if user has Standard plan for Google Calendar sync
  const hasStandardPlan = hasPlan('standard');
  
  // Filter members based on search query
  const filteredMembers = members.filter(member => 
    member.name.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
    member.role.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(memberSearchQuery.toLowerCase())
  );
  
  // Handle Google Calendar sync
  const handleSyncToGoogleCalendar = async () => {
    if (!hasStandardPlan) {
      toast.error("Google Calendar sync is available on Standard plan");
      return;
    }
    
    if (isGoogleConnected && isCalendarEnabled) {
      toast.success("Your calendar is already synced with Google Calendar");
      return;
    }
    
    setIsSyncingCalendar(true);
    try {
      await connectGoogle(`${window.location.origin}/dashboard`);
    } catch (error) {
      toast.error("Failed to connect to Google Calendar");
    } finally {
      setIsSyncingCalendar(false);
    }
  };
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    await Promise.all([
      fetchSponsorshipData(),
      fetchMembers(),
      fetchStats(),
      fetchUpcomingEvents(),
      checkExpenseAccess(),
    ]);
  };

  const fetchSponsorshipData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    // First check subscription.sponsored_by (from new Mollie flow)
    const { data: subscriptionData } = await supabase
      .from('subscriptions')
      .select('sponsored_by')
      .eq('pta_id', memberData.pta_id)
      .maybeSingle();

    if (subscriptionData?.sponsored_by) {
      setSubscriptionSponsoredBy(subscriptionData.sponsored_by);
    }

    // Also check sponsorships table for legacy/active sponsorships
    const { data } = await supabase
      .from('sponsorships')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    setSponsorshipData(data);
  };

  const fetchStats = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    // Get latest member
    const { data: latestMember } = await supabase
      .from('members')
      .select('full_name, user_id')
      .eq('pta_id', memberData.pta_id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    let latestMemberRole = 'Member';
    if (latestMember?.user_id) {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', latestMember.user_id);
      
      if (roles && roles.length > 0) {
        const roleMap: { [key: string]: string } = {
          'chair': 'Chair',
          'treasurer': 'Treasurer',
          'secretary': 'Secretary',
          'events': 'Events Coordinator',
          'catering': 'Catering Coordinator',
          'communications': 'Communications',
          'community': 'Community Liaison',
          'school_liaison': 'School Liaison',
          'trustee': 'Trustee',
          'shop': 'Shop Manager',
        };
        latestMemberRole = roleMap[roles[0].role] || roles[0].role;
      }
    }

    // Count upcoming events (next 30 days) - filtered by PTA
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    const { count: eventsCount } = await supabase
      .from('events')
      .select('*', { count: 'exact', head: true })
      .eq('pta_id', memberData.pta_id)
      .eq('status', 'active')
      .gte('event_date', new Date().toISOString())
      .lte('event_date', thirtyDaysFromNow.toISOString());

    // Get current financial year - filtered by PTA
    const { data: currentYear } = await supabase
      .from('financial_years')
      .select('id, start_date, end_date')
      .eq('pta_id', memberData.pta_id)
      .eq('is_current', true)
      .maybeSingle();

    let fundsRaised = 0;
    if (currentYear) {
      const { data: transactions } = await supabase
        .from('transactions')
        .select('amount')
        .eq('pta_id', memberData.pta_id)
        .eq('type', 'income')
        .gte('transaction_date', currentYear.start_date)
        .lte('transaction_date', currentYear.end_date);

      fundsRaised = transactions?.reduce((sum, t) => sum + Number(t.amount), 0) || 0;
    }

    // Fetch goals for the PTA
    const goalsQuery = await supabase
      .from('goals')
      .select('id, title, target_amount, current_amount, status, created_at')
      .eq('pta_id', memberData.pta_id)
      .order('created_at', { ascending: false });

    const allGoals = goalsQuery.data || [];

    setGoalsByStatus({
      active: allGoals.filter(g => g.status === 'active'),
      up_next: allGoals.filter(g => g.status === 'up-next'),
      planned: allGoals.filter(g => g.status === 'approved'),
      submitted: allGoals.filter(g => g.status === 'submitted')
    });

    // Count total members for the PTA
    const { count: totalMembersCount } = await supabase
      .from('members')
      .select('*', { count: 'exact', head: true })
      .eq('pta_id', memberData.pta_id)
      .eq('status', 'active');

    // Fetch active goals for Fundraising Progress card
    const activeGoalsData = allGoals.filter(g => g.status === 'active');
    setActiveGoals(activeGoalsData);

    setStats({
      latestMemberName: latestMember?.full_name || 'No members',
      latestMemberRole: latestMemberRole,
      upcomingEvents: eventsCount || 0,
      fundsRaised: `£${fundsRaised.toLocaleString()}`,
      totalMembers: totalMembersCount || 0
    });
  };

  const fetchUpcomingEvents = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    const { data } = await supabase
      .from('events')
      .select('id, name, event_date')
      .eq('pta_id', memberData.pta_id)
      .eq('status', 'active')
      .gte('event_date', new Date().toISOString())
      .order('event_date', { ascending: true })
      .limit(3);

    if (data) {
      setUpcomingEvents(data);
    }
  };

  const checkExpenseAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setCurrentUserId(user.id);

    // Get user's member record
    const { data: memberData } = await supabase
      .from('members')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData) return;

    // Check if they have expense access
    const { data: accessData } = await supabase
      .from('member_additional_access')
      .select('*')
      .eq('member_id', memberData.id)
      .eq('access_type', 'expenses')
      .maybeSingle();

    const hasAccess = !!accessData;
    setHasExpenseAccess(hasAccess);

    if (hasAccess) {
      fetchMyExpenseClaims();
    }
  };

  const fetchMyExpenseClaims = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('expense_claims')
      .select('*')
      .eq('submitted_by', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    if (data) {
      setMyExpenseClaims(data);
    }
  };

  const fetchMembers = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get the user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!memberData?.pta_id) return;

    // Fetch all members of the PTA
    const { data: membersData } = await supabase
      .from('members')
      .select('id, full_name, email, user_id, member_since')
      .eq('pta_id', memberData.pta_id);

    if (!membersData) return;

    // Get profiles and roles for each member
    const membersWithRoles = await Promise.all(
      membersData.map(async (member) => {
        // Get profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('hide_avatar_from_directory, avatar_url')
          .eq('id', member.user_id)
          .maybeSingle();

        // Skip if member opted out
        if (profile?.hide_avatar_from_directory) return null;

        // Get roles
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', member.user_id);

        const roleNames = roles?.map(r => {
          // Format role names nicely
          const roleMap: { [key: string]: string } = {
            'chair': 'Chair',
            'treasurer': 'Treasurer',
            'secretary': 'Secretary',
            'events': 'Events Coordinator',
            'catering': 'Catering Coordinator',
            'communications': 'Communications',
            'community': 'Community Liaison',
            'school_liaison': 'School Liaison',
            'trustee': 'Trustee',
            'shop': 'Shop Manager',
          };
          return roleMap[r.role] || r.role;
        }) || [];

        return {
          name: member.full_name,
          role: roleNames.length > 0 ? roleNames.join(', ') : 'Member',
          email: member.email,
          avatar_url: profile?.avatar_url,
          member_since: member.member_since
        };
      })
    );

    setMembers(membersWithRoles.filter(m => m !== null));
  };

  const statsDisplay = [
    {
      title: "Latest Member",
      value: stats.latestMemberName,
      description: stats.latestMemberRole,
      icon: Users,
    },
    {
      title: "Upcoming Events",
      value: stats.upcomingEvents.toString(),
      description: "In the next 30 days",
      icon: Calendar,
    },
    {
      title: "Funds Raised",
      value: stats.fundsRaised,
      description: "This year",
      icon: DollarSign,
    },
    {
      title: "Members",
      value: stats.totalMembers.toString(),
      description: "Total members",
      icon: Users,
    },
  ];


  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold text-foreground">PTA Dashboard</h1>
        {/* Show sponsored by text from subscription (Mollie payment flow) */}
        {subscriptionSponsoredBy && (
          <p className="text-lg">
            <span className="font-semibold">Subscription kindly sponsored by:</span>{' '}
            <span className="font-normal">{subscriptionSponsoredBy}</span>
          </p>
        )}
      </div>

      {/* Show legacy sponsorship banner if no subscription sponsored_by */}
      {!subscriptionSponsoredBy && sponsorshipData && (
        <Card className="shadow-card border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="pt-6">
            <p className="text-center text-lg">
              <span className="text-muted-foreground">Membership Sponsored by:</span>{' '}
              <span className="font-bold text-primary">{sponsorshipData.business_name}</span>
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {statsDisplay.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5">
                <CardTitle className="text-xs font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-1">
                <div className="text-xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="members">Members Directory</TabsTrigger>
          {showDocumentsTab && (
            <TabsTrigger value="documents">Documents</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Fundraising Progress</CardTitle>
                <CardDescription>Active goals</CardDescription>
              </CardHeader>
              <CardContent>
                {activeGoals.length > 0 ? (
                  <div className="space-y-4">
                    {activeGoals.map((goal) => {
                      const progress = goal.target_amount > 0 
                        ? (Number(goal.current_amount || 0) / Number(goal.target_amount)) * 100 
                        : 0;
                      return (
                        <div key={goal.id}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{goal.title}</span>
                            <span className="text-sm text-primary">
                              £{Number(goal.current_amount || 0).toLocaleString()} / £{Number(goal.target_amount).toLocaleString()}
                            </span>
                          </div>
                          <div className="h-2 bg-secondary rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary transition-all" 
                              style={{ width: `${Math.min(progress, 100)}%` }} 
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground">
                      No active goals yet
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Goals</CardTitle>
                <CardDescription>Goals by status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => setSelectedGoalStatus('active')}
                      className={`p-3 border rounded-lg transition-all cursor-pointer ${
                        selectedGoalStatus === 'active' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-2xl font-bold text-primary">{goalsByStatus.active.length}</div>
                      <div className="text-xs text-muted-foreground">Active</div>
                    </button>
                    <button 
                      onClick={() => setSelectedGoalStatus('up_next')}
                      className={`p-3 border rounded-lg transition-all cursor-pointer ${
                        selectedGoalStatus === 'up_next' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-2xl font-bold text-primary">{goalsByStatus.up_next.length}</div>
                      <div className="text-xs text-muted-foreground">Up Next</div>
                    </button>
                    <button 
                      onClick={() => setSelectedGoalStatus('planned')}
                      className={`p-3 border rounded-lg transition-all cursor-pointer ${
                        selectedGoalStatus === 'planned' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-2xl font-bold text-primary">{goalsByStatus.planned.length}</div>
                      <div className="text-xs text-muted-foreground">Planned</div>
                    </button>
                    <button 
                      onClick={() => setSelectedGoalStatus('submitted')}
                      className={`p-3 border rounded-lg transition-all cursor-pointer ${
                        selectedGoalStatus === 'submitted' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-2xl font-bold text-primary">{goalsByStatus.submitted.length}</div>
                      <div className="text-xs text-muted-foreground">Submitted</div>
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {(() => {
                      const statusGoals = goalsByStatus[selectedGoalStatus];
                      const statusLabels = {
                        active: 'Active',
                        up_next: 'Up Next',
                        planned: 'Planned',
                        submitted: 'Submitted'
                      };
                      
                      if (statusGoals.length === 0) {
                        return (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No {statusLabels[selectedGoalStatus].toLowerCase()} goals yet
                          </p>
                        );
                      }
                      
                      const getStatusIcon = () => {
                        switch (selectedGoalStatus) {
                          case 'active':
                            return <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />;
                          case 'up_next':
                            return <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />;
                          case 'planned':
                            return <CheckSquare className="h-4 w-4 text-purple-600 flex-shrink-0" />;
                          case 'submitted':
                            return <Calendar className="h-4 w-4 text-orange-600 flex-shrink-0" />;
                        }
                      };
                      
                      return (
                        <div className="space-y-2">
                          <h4 className="text-sm font-semibold text-muted-foreground">{statusLabels[selectedGoalStatus]}</h4>
                          {statusGoals.map((goal: any) => (
                            <div key={goal.id} className="flex items-center gap-2 p-2 rounded-lg">
                              {getStatusIcon()}
                              <div className="flex-1">
                                <p className="font-medium">{goal.title}</p>
                                <p className="text-sm text-muted-foreground">
                                  Target: £{Number(goal.target_amount).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>

            {hasExpenseAccess && (
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>My Expense Claims</CardTitle>
                  <CardDescription>Your outstanding claims</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myExpenseClaims.length > 0 ? (
                      myExpenseClaims.map((claim) => (
                        <div key={claim.id} className="p-3 border border-border rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{claim.description}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              claim.status === "paid" 
                                ? "bg-green-100 text-green-800" 
                                : claim.status === "approved"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}>
                              {claim.status}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            Submitted {format(new Date(claim.created_at), 'MMM d, yyyy')}
                          </p>
                          <p className="text-sm font-semibold text-primary">£{Number(claim.amount).toFixed(2)}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No expense claims yet
                      </p>
                    )}
                  </div>
                  <Button 
                    className="w-full mt-4" 
                    onClick={() => setShowExpenseDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Submit New Claim
                  </Button>
                </CardContent>
              </Card>
            )}




            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Upcoming Events</CardTitle>
                <CardDescription>Next 3 scheduled events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map((event) => (
                      <div key={event.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div className="flex items-start space-x-3">
                          <Calendar className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <p className="font-medium">{event.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(event.event_date), 'MMMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <HelpingButton eventId={event.id} />
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No upcoming events
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="calendar" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>PTA Calendar</CardTitle>
              <CardDescription>Auto-synced events and meetings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {hasStandardPlan && (
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">Calendar Sync</h4>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleSyncToGoogleCalendar}
                        disabled={isSyncingCalendar}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        {isGoogleConnected && isCalendarEnabled 
                          ? "Synced with Google" 
                          : isSyncingCalendar 
                            ? "Connecting..." 
                            : "Sync to Google Calendar"}
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {isGoogleConnected && isCalendarEnabled
                        ? "Your PTA events and meetings are syncing to Google Calendar"
                        : "Automatically sync PTA events and meetings to your personal calendar"}
                    </p>
                  </div>
                )}

                <div className="border border-border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Upcoming Events</h4>
                  <div className="space-y-3">
                    {upcomingEvents.length > 0 ? (
                      upcomingEvents.map((event) => {
                        const eventDate = new Date(event.event_date);
                        return (
                          <div key={event.id} className="flex items-center gap-3 p-3 bg-accent/50 rounded">
                            <div className="text-center min-w-[50px]">
                              <div className="text-2xl font-bold">{format(eventDate, 'd')}</div>
                              <div className="text-xs text-muted-foreground uppercase">{format(eventDate, 'MMM')}</div>
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{event.name}</p>
                              <p className="text-sm text-muted-foreground">{format(eventDate, 'EEEE')}</p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No upcoming events
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="space-y-3">
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Members Directory</CardTitle>
                  <CardDescription>{members.length} members (members can opt-out in settings)</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search members..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((member, i) => (
                    <div key={i} className="p-5 border border-border rounded-lg hover:shadow-md transition-shadow bg-card">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16 border-2 border-primary/20">
                          <AvatarImage src={member.avatar_url} alt={member.name} />
                          <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/5 text-primary text-lg font-semibold">
                            {member.name.split(' ').map((n: string) => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-foreground truncate">{member.name}</h4>
                          <p className="text-sm text-muted-foreground truncate">{member.role}</p>
                        </div>
                      </div>
                      {member.member_since && (
                        <div className="mt-4 pt-3 border-t border-border">
                          <p className="text-sm text-muted-foreground">
                            Member since {format(new Date(member.member_since), 'MMMM yyyy')}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    {memberSearchQuery ? "No members found matching your search" : "No members found in the directory"}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {showDocumentsTab && (
          <TabsContent value="documents" className="space-y-3">
            <DocumentsTabContent 
              ptaId={documentsPtaId} 
              isReadOnly={isDocumentsReadOnly}
              isGoogleConnected={isGoogleConnected}
              isDriveEnabled={isDriveEnabled}
              onConnectGoogle={() => connectGoogle(`${window.location.origin}/dashboard?tab=documents`)}
            />
          </TabsContent>
        )}
      </Tabs>

      <SubmitExpenseClaimDialog
        open={showExpenseDialog}
        onOpenChange={setShowExpenseDialog}
        onSuccess={fetchMyExpenseClaims}
      />
    </div>
  );
}
